import React, { useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import Page from "~/components/Page";
import {
  Display01,
  Paragraph01,
  Paragraph03,
  Paragraph04,
  Label01,
} from "~/components/Texts";
import Logo from "~/components/logo";
import {
  Box,
  Flex,
  Stack,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormErrorMessage,
  useToast,
  Button,
  Input,
  useColorModeValue,
  LightMode,
} from "@chakra-ui/react";
import { FiChevronRight } from "react-icons/fi";
import { FaFacebookF, FaGoogle, FaTwitter } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { Formik, Field } from "formik";
import { Desktop } from "~/components/MediaQuery";
import { validateEmail } from "~/utils/InputValidators";
import {
  authWithEmailLink,
  authWithGoogle,
  authWithFacebook,
  authWithTwitter,
} from "~/actions/AuthActions";
import ConfirmEmail from "~/components/animations/ConfirmEmail";
import MotionBox from "~/components/MotionBox";
import { useTheme } from "@chakra-ui/react";
import Img from "react-optimized-image";
import Svg from "react-optimized-image";
import SideImage from "~/public/login_side_image.jpg";
import BottomPattern from "~/public/marketing/login_bottom_pattern.svg";

export default function LogIn() {
  return (
    <Page title="Inicio de sesión | Shop Story">
      <LightMode>
        <Box position="fixed">
          <Background />
        </Box>
        <Flex
          position="absolute"
          width="100vw"
          minHeight="100vh"
          justify="center"
          align="center"
        >
          <Foreground />
        </Flex>
      </LightMode>
    </Page>
  );
}

function Background() {
  return (
    <Flex minHeight="100vh" minWidth="100vw">
      {/* Left Panel */}
      <Box flex="2" backgroundColor={"light.background.strong"} />

      {/* Right Panel  */}
      <Desktop flex="1" position="relative">
        <Img
          style={{
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "0% 0%",
          }}
          src={SideImage}
          webp
          sizes={[256, 450, 640]}
          densities={[1, 2]} // Retina support
          draggable="false"
          loading="lazy"
        />
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          backgroundColor="rgba(0,0,0,0.2)"
        />
      </Desktop>
    </Flex>
  );
}

function Foreground() {
  return (
    <Flex
      direction="column"
      justify="start"
      minHeight="100vh"
      width={{ mobile: "90%", desktop: "80%" }}
      maxW="1200px"
    >
      <Box pt="12" pb="6">
        <Link href="/">
          <Button variant="unstyled">
            <Logo />
          </Button>
        </Link>
      </Box>

      <Flex flex="1" justify="center" alignItems="center" pb="6">
        <Desktop flex="1">
          <LeftPanel />
        </Desktop>
        <RightPanel />
      </Flex>
    </Flex>
  );
}

function LeftPanel() {
  const variants = {
    left: { x: -100, opacity: 0 },
    center: { x: 0, opacity: 1 },
  };

  return (
    <MotionBox initial="left" animate="center" variants={variants}>
      <Stack spacing="6" pr="6" color="light.brand.strong">
        <Display01>Te extrañamos</Display01>
        <Paragraph01>
          <b>Todos los días se aprende algo nuevo. </b>
          <br /> Por eso hoy te vamos a ayudar con estos tips:
        </Paragraph01>
        <Stack>
          <Tip>
            <b>Mostrá videos en lugar de imágenes en tus productos</b>{" "}
            seleccionando GIFs cómo imagen de publicación para cada producto
          </Tip>
          <Tip>
            <b>Pausá productos en lugar de borralos</b> desde el producto en tu
            panel de control
          </Tip>
          <Tip>
            <b>Tu tienda se adapta automáticamente para dispositivos móviles</b>
            , los bloques de diseño que ocupen menos de la mitad de la pantalla
            en la vista de diseño ocuparán media pantalla en dispositivos
            móviles. Los demás bloques ocuparán la pantalla completa.
          </Tip>
        </Stack>
      </Stack>
      <Box height="64px" pt="8">
        <Svg src={BottomPattern} />
      </Box>
    </MotionBox>
  );
}

function Tip(props) {
  return (
    <Stack direction="row" align="flex-start">
      <Box pt="1">
        <FiChevronRight size="20" />
      </Box>
      <Paragraph03 maxWidth="450px">{props.children}</Paragraph03>
    </Stack>
  );
}

function RightPanel() {
  const variants = {
    bottom: { y: 100, opacity: 0 },
    center: { y: 0, opacity: 1 },
  };
  const router = useRouter();
  const toast = useToast();
  const [doingAuth, setDoingAuth] = useState(false);
  const [otpEmailSent, setOtpEmailSent] = useState(false);

  function doLoginWithEmail(formValues, formikActions) {
    setDoingAuth(true);

    authWithEmailLink(formValues.email)
      .then(function () {
        formikActions.setSubmitting(false);
        setDoingAuth(false);
        setOtpEmailSent(true);
      })
      .catch(function (error) {
        formikActions.setSubmitting(false);
        setDoingAuth(false);

        if (
          error.code === "auth/email-already-in-use" ||
          error.code === "auth/account-exists-with-different-credential"
        )
          showErrorToast(
            "Ya existe una cuenta registrada para " + formValues.email
          );
        else
          showErrorToast(
            "No pudimos iniciar sesión con tu cuenta en este momento"
          );
      });
  }

  function doSocialLoginWithGoogle() {
    setDoingAuth(true);
    authWithGoogle()
      .then(function () {
        setDoingAuth(false);
        showSuccesToast();
        router.replace("/my-shops");
      })
      .catch(function (error) {
        setDoingAuth(false);
        if (
          error.code === "auth/email-already-in-use" ||
          error.code === "auth/account-exists-with-different-credential"
        )
          showErrorToast(
            "Ya existe una cuenta registrada con el email de tu cuenta de Google con otro método de inicio de sesión"
          );
        else showErrorToast("No pudimos crear tu cuenta en este momento");
      });
  }

  function doSocialLoginWithFacebook() {
    setDoingAuth(true);
    authWithFacebook()
      .then(function () {
        setDoingAuth(false);
        showSuccesToast();
        router.replace("/my-shops");
      })
      .catch(function (error) {
        if (
          error.code === "auth/email-already-in-use" ||
          error.code === "auth/account-exists-with-different-credential"
        )
          showErrorToast(
            "Ya existe una cuenta registrada con el email de tu cuenta de Facebook"
          );
        else showErrorToast("No pudimos crear tu cuenta en este momento");
        setDoingAuth(false);
      });
  }

  function doSocialLoginWithTwitter() {
    setDoingAuth(true);
    authWithTwitter()
      .then(function () {
        setDoingAuth(false);
        showSuccesToast();
        router.replace("/my-shops");
      })
      .catch(function (error) {
        if (
          error.code === "auth/email-already-in-use" ||
          error.code === "auth/account-exists-with-different-credential"
        )
          showErrorToast(
            "Ya existe una cuenta registrada con el email de tu cuenta de Twitter"
          );
        else showErrorToast("No pudimos crear tu cuenta en este momento");
        setDoingAuth(false);
      });
  }

  function showSuccesToast() {
    toast({
      title: "¡Bienvenido!",
      description: "Has iniciado sesión",
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  }

  function showErrorToast(rationale) {
    toast({
      title: "Upss",
      description: rationale,
      status: "error",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  }

  return (
    <Box flex="1" maxWidth="500px" width="100%">
      <MotionBox initial="bottom" animate="center" variants={variants}>
        <Box
          backgroundColor={useColorModeValue(
            "light.background.superLite",
            "dark.background.superLite"
          )}
          borderRadius="brand"
          p="6"
          boxShadow="md"
        >
          <Stack>
            {!otpEmailSent && <FormHeader />}
            {!otpEmailSent && (
              <Form
                onSubmit={(values, actions) =>
                  doLoginWithEmail(values, actions)
                }
                onGoogle={() => doSocialLoginWithGoogle()}
                onFacebook={() => doSocialLoginWithFacebook()}
                onTwitter={() => doSocialLoginWithTwitter()}
                doingAuth={doingAuth}
              />
            )}
            {otpEmailSent && <OtpEmailSent />}
          </Stack>
        </Box>
      </MotionBox>
    </Box>
  );
}

function FormHeader() {
  return (
    <Flex
      width="100%"
      align="center"
      justify="space-between"
      color="light.brand.strong"
    >
      <Label01>Inicio de sesión</Label01>
      <Link href="/auth/signup">
        <Button variant="link">Crear cuenta</Button>
      </Link>
    </Flex>
  );
}

function Form(props) {
  const {
    onSubmit = () => {},
    onFacebook = () => {},
    onGoogle = () => {},
    onTwitter = () => {},
    doingAuth,
  } = props;
  const chakraTheme = useTheme();

  return (
    <Formik
      initialValues={{}}
      onSubmit={(values, actions) => {
        actions.setSubmitting(true);
        onSubmit(values, actions);
      }}
    >
      {(props) => (
        <form onSubmit={props.handleSubmit} style={{ width: "100%" }}>
          <Stack spacing="4" pt="4">
            <Field name="email" validate={validateEmail}>
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.email && form.touched.email}
                >
                  <InputGroup size="lg">
                    <InputLeftElement
                      children={
                        <MdEmail
                          color={useColorModeValue(
                            chakraTheme.colors.light.brand.strong,
                            chakraTheme.colors.dark.brand.strong
                          )}
                        />
                      }
                    />
                    <Input
                      {...field}
                      type="email"
                      placeholder="Dirección de correo"
                    />
                  </InputGroup>
                  <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Button
              type="submit"
              width="100%"
              size="lg"
              variant="solid"
              isLoading={doingAuth}
            >
              Iniciar sesión
            </Button>

            <SocialLogin
              isDisabled={doingAuth}
              onFacebook={() => onFacebook()}
              onGoogle={() => onGoogle()}
              onTwitter={() => onTwitter()}
            />
          </Stack>
        </form>
      )}
    </Formik>
  );
}

function SocialLogin({
  isDisabled = false,
  onFacebook = () => {},
  onGoogle = () => {},
  onTwitter = () => {},
}) {
  return (
    <Stack align="center" width="100%" spacing="4" pt="2">
      <Paragraph04>O inciá sesión con</Paragraph04>
      <Stack direction="row" flexWrap="wrap" width="100%" justify="center">
        {/* At the moment (26-08-2020), the Facebook App has been disabled due to compliance issues. We'll enable back this later.*/}
        {false && (
          <Button
            size="md"
            leftIcon={<FaFacebookF />}
            variant="outline"
            isDisabled={isDisabled}
            mb="2"
            onClick={() => onFacebook()}
          >
            Facebook
          </Button>
        )}
        <Button
          size="md"
          leftIcon={<FaGoogle />}
          variant="outline"
          isDisabled={isDisabled}
          mb="2"
          onClick={() => onGoogle()}
        >
          Google
        </Button>
        <Button
          size="md"
          leftIcon={<FaTwitter />}
          variant="outline"
          isDisabled={isDisabled}
          mb="2"
          onClick={() => onTwitter()}
        >
          Twitter
        </Button>
      </Stack>
    </Stack>
  );
}

function OtpEmailSent() {
  return (
    <Stack spacing="6" width="100%">
      <Label01>Revisá tu correo</Label01>
      <ConfirmEmail />
      <Paragraph03 text-align="center" width="100%">
        <b>¡Todo listo!</b>
        <br />
        Acabamos de enviarte un correo electrónico con un enlace para iniciar tu
        sesión. No necesitas una contraseña :)
      </Paragraph03>
    </Stack>
  );
}
